import config from "../../config";
import type {
  ParamValue,
  RequestConfig,
  RequestInterceptor,
  ResponseInterceptor,
} from "./types";
import { formatApiErrorMessage } from "@/shared/utils/apiErrorMessage";

export class HTTP {
  private baseURL: string;
  private defaultTimeout: number;
  private requestInterceptors: RequestInterceptor[] = [];
  private responseInterceptors: ResponseInterceptor[] = [];
  private getAuthToken: (() => string | null) | null = null;
  private getTenantId: (() => string | null) | null = null;
  private onUnauthorized: (() => void) | null = null;

  constructor(baseURL?: string | URL) {
    this.baseURL = baseURL
      ? typeof baseURL === "string"
        ? baseURL
        : baseURL.toString()
      : config.API_BASE_URL || "";
    this.defaultTimeout = config.REQUEST_TIMEOUT || 30000;
  }

  /**
   * Set authentication token getter
   */
  setAuthTokenGetter(getter: () => string | null): void {
    this.getAuthToken = getter;
  }

  /**
   * Set tenant id getter (sent as x-tenantid)
   */
  setTenantIdGetter(getter: () => string | null): void {
    this.getTenantId = getter;
  }

  /**
   * Set unauthorized callback
   */
  setUnauthorizedHandler(handler: () => void): void {
    this.onUnauthorized = handler;
  }

  /**
   * Add request interceptor
   */
  useRequestInterceptor(handler: RequestInterceptor): void {
    this.requestInterceptors.push(handler);
  }

  /**
   * Add response interceptor
   */
  useResponseInterceptor(handler: ResponseInterceptor): void {
    this.responseInterceptors.push(handler);
  }

  /**
   * Build URL with query parameters (array values become repeated keys, e.g. status=Open&status=Monitored)
   */
  private buildURL(url: string, params?: Record<string, ParamValue>): string {
    if (!params || Object.keys(params).length === 0) {
      return url;
    }

    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((v) => searchParams.append(key, String(v)));
      } else {
        searchParams.append(key, String(value));
      }
    });

    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}${searchParams.toString()}`;
  }

  /**
   * Build headers with authentication
   */
  private buildHeaders(
    customHeaders?: HeadersInit,
    skipAuth = false,
  ): HeadersInit {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    // Merge custom headers
    if (customHeaders) {
      if (customHeaders instanceof Headers) {
        customHeaders.forEach((value, key) => {
          headers[key] = value;
        });
      } else if (Array.isArray(customHeaders)) {
        customHeaders.forEach(([key, value]) => {
          headers[key] = value;
        });
      } else {
        Object.assign(headers, customHeaders);
      }
    }

    if (skipAuth) {
      return headers;
    }

    // Add auth token if available
    if (this.getAuthToken) {
      const token = this.getAuthToken();
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
    }

    // Add tenant id if available
    if (this.getTenantId) {
      const tenantId = this.getTenantId();
      if (tenantId) {
        headers["x-tenantid"] = tenantId;
      }
    }

    return headers;
  }

  /**
   * Handle 401 Unauthorized responses (UI redirect handled by app layer).
   */
  private handleUnauthorized(): void {
    this.onUnauthorized?.();
  }

  /**
   * Apply request interceptors
   */
  private async applyRequestInterceptors(
    config: RequestInit,
    url: string,
  ): Promise<RequestInit> {
    let result = config;
    for (const interceptor of this.requestInterceptors) {
      result = await interceptor(result, url);
    }
    return result;
  }

  /**
   * Apply response interceptors
   */
  private async applyResponseInterceptors(
    response: Response,
  ): Promise<Response> {
    let result = response;
    for (const interceptor of this.responseInterceptors) {
      result = await interceptor(result);
    }
    return result;
  }

  /**
   * Make HTTP request
   */
  private async request<T = unknown>(
    endpoint: string,
    method: string,
    requestConfig?: RequestConfig,
  ): Promise<T> {
    if (!this.baseURL) {
      throw new Error("API_BASE_URL is not configured");
    }

    const {
      params,
      headers: customHeaders,
      returnRawResponse = false,
      timeout: _timeout = this.defaultTimeout,
      body,
      skipAuth = false,
    } = requestConfig || {};

    // Build full URL
    const fullURL = this.buildURL(`${this.baseURL}${endpoint}`, params);

    // Build headers
    const headers = this.buildHeaders(customHeaders, skipAuth);
    // Setup abort controller for timeout
    const controller = new AbortController();
    // const timeoutId = setTimeout(() => controller.abort(), timeout);

    // Build request options
    let requestOptions: RequestInit = {
      method,
      headers,
      credentials: "omit",
      signal: controller.signal,
    };

    // Add body for non-GET requests
    if (body && method !== "GET") {
      // Handle FormData - don't stringify it, and let browser set Content-Type
      if (body instanceof FormData) {
        requestOptions.body = body;
        // Remove Content-Type header for FormData - browser will set it with boundary
        delete (headers as Record<string, string>)["Content-Type"];
      } else {
        requestOptions.body =
          typeof body === "string" ? body : JSON.stringify(body);
      }
    }

    // Apply request interceptors
    requestOptions = await this.applyRequestInterceptors(
      requestOptions,
      fullURL,
    );

    try {
      const response = await fetch(fullURL, requestOptions);
      // Apply response interceptors
      const processedResponse = await this.applyResponseInterceptors(response);

      // Handle 401 Unauthorized
      // Don't show session expired popup for auth endpoints (login/register)
      // These should just return errors, not trigger session expiration flow
      if (processedResponse.status === 401) {
        const isAuthEndpoint =
          endpoint.includes("/auth/login") ||
          endpoint.includes("/auth/sign-up") ||
          endpoint.includes("/auth/register") ||
          endpoint.includes("/platform/tenant/resolve");

        if (!isAuthEndpoint) {
          this.handleUnauthorized();
          throw new Error("Session expired. Please login again.");
        }
        // For auth endpoints, just throw the error without popup/redirect
      }

      // Handle errors
      if (!processedResponse.ok) {
        const errorData = await processedResponse.json().catch(() => ({}));
        const payload = errorData as {
          message?: unknown;
          error?: unknown;
        };
        const rawMessage =
          payload.message ??
          payload.error ??
          `Request failed with status ${processedResponse.status}`;
        throw new Error(formatApiErrorMessage(rawMessage));
      }

      // Return raw response if requested
      if (returnRawResponse) {
        return processedResponse as unknown as T;
      }

      // Parse JSON response. Long-running AI endpoints prefix the body with
      // newline heartbeats, and send only heartbeats if they fail mid-stream.
      const text = (await processedResponse.text()).trim();
      const data = text ? JSON.parse(text) : {};

      return data as T;
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") {
        throw new Error("Request timeout. Please try again.");
      }

      throw error;
    }
  }

  /**
   * GET request
   */
  async get<
    ResponseType,
    ParamsType extends Record<string, ParamValue> = Record<string, ParamValue>,
  >(
    endpoint: string,
    request?: RequestConfig & { params?: ParamsType },
  ): Promise<ResponseType> {
    return this.request<ResponseType>(endpoint, "GET", request);
  }

  /**
   * POST request
   */
  async post<
    ResponseType,
    BodyType = unknown,
    ParamsType extends Record<string, ParamValue> = Record<string, ParamValue>,
  >(
    endpoint: string,
    request?: RequestConfig & { body?: BodyType; params?: ParamsType },
  ): Promise<ResponseType> {
    return this.request<ResponseType>(endpoint, "POST", request);
  }

  /**
   * PUT request
   */
  async put<
    ResponseType,
    BodyType = unknown,
    ParamsType extends Record<string, ParamValue> = Record<string, ParamValue>,
  >(
    endpoint: string,
    request?: RequestConfig & { body?: BodyType; params?: ParamsType },
  ): Promise<ResponseType> {
    return this.request<ResponseType>(endpoint, "PUT", request);
  }

  /**
   * PATCH request
   */
  async patch<
    ResponseType,
    BodyType = unknown,
    ParamsType extends Record<string, ParamValue> = Record<string, ParamValue>,
  >(
    endpoint: string,
    request?: RequestConfig & { body?: BodyType; params?: ParamsType },
  ): Promise<ResponseType> {
    return this.request<ResponseType>(endpoint, "PATCH", request);
  }

  /**
   * DELETE request
   */
  async delete<
    ResponseType,
    ParamsType extends Record<string, ParamValue> = Record<string, ParamValue>,
  >(
    endpoint: string,
    request?: RequestConfig & { params?: ParamsType },
  ): Promise<ResponseType> {
    return this.request<ResponseType>(endpoint, "DELETE", request);
  }
}
